import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';
import userEvent from '@testing-library/user-event';

import ConfigProvider from 'components/ConfigProvider';
import { NotificationsPanel } from '@decisiv/ui-components';

import useAddToAccount from './useAddToAccount';

let hook;

const Probe = () => {
  hook = useAddToAccount(7, { onAdded: Probe.onAdded });

  return (
    <div>
      <span data-testid="is-open">{String(hook.isOpen)}</span>
      <span data-testid="options">
        {hook.accountOptions.map((o) => o.label).join(',')}
      </span>
      <span data-testid="selected">{hook.selectedAccountIds.join(',')}</span>
      <span data-testid="submitting">{String(hook.isSubmitting)}</span>
      <span data-testid="error">{hook.modalError ?? ''}</span>
      <button type="button" onClick={() => hook.open()}>
        open
      </button>
      <button type="button" onClick={() => hook.close()}>
        close
      </button>
      <button type="button" onClick={() => hook.setSelectedAccountIds(['9'])}>
        select
      </button>
      <button type="button" onClick={() => hook.submit()}>
        submit
      </button>
    </div>
  );
};

const respondTo = ({ assignable = { accounts: [], total: 0 }, add }) => {
  global.fetch = jest.fn((url, options) => {
    if (String(url).includes('assignable_accounts')) {
      return Promise.resolve({ ok: true, json: async () => assignable });
    }

    if (String(url).includes('add_account_access')) {
      const outcome = add || { ok: true, body: { accountAccess: [] } };

      return Promise.resolve({
        ok: outcome.ok !== false,
        status: outcome.status || 200,
        json: async () => outcome.body,
      });
    }

    return Promise.reject(new Error(`unexpected fetch: ${url} ${options}`));
  });
};

const renderProbe = (onAdded) => {
  Probe.onAdded = onAdded;

  return render(
    <ConfigProvider>
      <Probe />
      <NotificationsPanel marginTop={4} zIndex={1000} />
    </ConfigProvider>,
  );
};

beforeEach(() => {
  document.head.innerHTML = '<meta name="csrf-token" content="a-token" />';
});

afterEach(() => {
  hook = undefined;
  Probe.onAdded = undefined;
  document.head.innerHTML = '';
  delete global.fetch;
});

describe('management/EditUser useAddToAccount', () => {
  it('starts closed', () => {
    respondTo({});
    renderProbe();

    expect(screen.getByTestId('is-open')).toHaveTextContent('false');
  });

  it('opens and loads the first page of assignable accounts', async () => {
    respondTo({
      assignable: { accounts: [{ label: 'Alpha Fleet', value: 9 }], total: 1 },
    });
    renderProbe();

    userEvent.click(screen.getByText('open'));

    expect(screen.getByTestId('is-open')).toHaveTextContent('true');
    await waitFor(() =>
      expect(screen.getByTestId('options')).toHaveTextContent('Alpha Fleet'),
    );
  });

  it('resets the selection and error on close', async () => {
    respondTo({});
    renderProbe();

    userEvent.click(screen.getByText('open'));
    userEvent.click(screen.getByText('select'));
    expect(screen.getByTestId('selected')).toHaveTextContent('9');

    userEvent.click(screen.getByText('close'));

    expect(screen.getByTestId('is-open')).toHaveTextContent('false');
    expect(screen.getByTestId('selected')).toHaveTextContent('');
  });

  it('does nothing when submitted with no selection', async () => {
    respondTo({});
    renderProbe();

    userEvent.click(screen.getByText('open'));
    userEvent.click(screen.getByText('submit'));

    await waitFor(() =>
      expect(
        global.fetch.mock.calls.filter(([url]) =>
          String(url).includes('add_account_access'),
        ),
      ).toHaveLength(0),
    );
  });

  describe('submitting a selection', () => {
    it('sends the selected ids to the add endpoint', async () => {
      respondTo({ add: { ok: true, body: { accountAccess: [] } } });
      renderProbe();

      userEvent.click(screen.getByText('open'));
      userEvent.click(screen.getByText('select'));
      userEvent.click(screen.getByText('submit'));

      await waitFor(() =>
        expect(global.fetch).toHaveBeenCalledWith(
          '/management/users/7/add_account_access',
          expect.objectContaining({
            body: JSON.stringify({ account_ids: ['9'] }),
          }),
        ),
      );
    });

    it('reports the refreshed accountAccess to onAdded, toasts, and closes', async () => {
      const accountAccess = [{ id: 9, name: 'Alpha Fleet', removedAt: null }];
      const onAdded = jest.fn();
      respondTo({ add: { ok: true, body: { accountAccess } } });
      renderProbe(onAdded);

      userEvent.click(screen.getByText('open'));
      userEvent.click(screen.getByText('select'));
      userEvent.click(screen.getByText('submit'));

      expect(
        await screen.findByText('Account access added.'),
      ).toBeInTheDocument();
      expect(onAdded).toHaveBeenCalledWith(accountAccess);
      expect(screen.getByTestId('is-open')).toHaveTextContent('false');
    });

    it('keeps the modal open and shows the message inline on failure', async () => {
      respondTo({
        add: {
          ok: false,
          status: 422,
          body: { errors: { base: ['Choose at least one account to add.'] } },
        },
      });
      renderProbe();

      userEvent.click(screen.getByText('open'));
      userEvent.click(screen.getByText('select'));
      userEvent.click(screen.getByText('submit'));

      expect(await screen.findByTestId('error')).toHaveTextContent(
        'Choose at least one account to add.',
      );
      expect(screen.getByTestId('is-open')).toHaveTextContent('true');
      expect(
        screen.queryByText('Account access added.'),
      ).not.toBeInTheDocument();
    });

    it('shows a generic message on a failure with nothing to say', async () => {
      respondTo({ add: { ok: false, status: 500, body: {} } });
      renderProbe();

      userEvent.click(screen.getByText('open'));
      userEvent.click(screen.getByText('select'));
      userEvent.click(screen.getByText('submit'));

      expect(await screen.findByTestId('error')).toHaveTextContent(
        'Something went wrong. Please try again.',
      );
    });
  });
});
